import { GifType } from '../gifSearches/types';
import {
  FileReference,
  FileUploadJobReference,
  ImageFileReference,
  SharePointFileLinkReference,
  SharePointVideoLinkReference,
  SharePointWebLinkReference,
  VideoFileReference,
  WebImageReference,
  WebLinkReference,
  WebVideoReference,
} from '../types';
import { WebLink } from '../webLinks/types';

export enum AttachmentMenuItemState {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
  Hidden = 'Hidden',
}

export enum AttachmentsAllowedStatus {
  AllAllowed = 'AllAllowed',
  DestinationNotSelected = 'DestinationNotSelected',
  DisabledForClient = 'DisabledForClient',
  DisabledForNetwork = 'DisabledForNetwork',
  GroupNotSelected = 'GroupNotSelected',
  HiddenForPostOnBehalfOfUser = 'HiddenForPostOnBehalfOfUser',
  IsEditing = 'IsEditing',
  MaxAttachments = 'MaxAttachments',
  MediaOnlyAllowed = 'MediaOnlyAllowed',
  NoUploadInPM = 'NoUploadInPM',
  NetworkSettingsUnavailable = 'NetworkSettingsUnavailable',
  ThreadLevelGuest = 'ThreadLevelGuest',
  UserLevel = 'UserLevel',
  ViewerCommunityRelationshipUnknown = 'ViewerCommunityRelationshipUnknown',
  DisallowedForPublisherType = 'DisallowedForPublisherType',
}

export interface PublisherFileReference extends FileReference {
  readonly wasAttachedViaUpload: boolean;
}

export interface PublisherImageFileReference extends ImageFileReference {
  readonly wasAttachedViaUpload: boolean;
}

export interface PublisherVideoFileReference extends VideoFileReference {
  readonly wasAttachedViaUpload: boolean;
}

export interface PublisherWebLinkReference extends WebLinkReference {
  readonly attachedViaMRU: boolean;
  readonly isAttachedViaFilePicker: boolean;
}

export interface PublisherSharePointFileLinkReference extends SharePointFileLinkReference {
  readonly isAttachedViaFilePicker: boolean;
}

export interface PublisherSharePointVideoLinkReference extends SharePointVideoLinkReference {
  readonly isAttachedViaFilePicker: boolean;
}

export interface PublisherSharePointWebLinkReference extends SharePointWebLinkReference {
  readonly isAttachedViaFilePicker: boolean;
}

export interface ResolvingWebLinkReference {
  readonly id: WebLink['url'];
  readonly schema: 'resolvingWebLinks';
}

export const isPublisherFileAttachmentReference = (
  reference: PublisherAttachmentReference
): reference is PublisherFileReference | PublisherImageFileReference | PublisherVideoFileReference =>
  reference.schema === 'files' || reference.schema === 'imageFiles' || reference.schema === 'videoFiles';

export const isPublisherSharePointAttachmentReference = (
  reference: PublisherAttachmentReference
): reference is PublisherSharePointFileLinkReference | PublisherSharePointVideoLinkReference =>
  reference.schema === 'sharePointFileLinks' || reference.schema === 'sharePointVideoLinks';

export const isPublisherWebLinkReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebLinkReference => reference.schema === 'webLinks';

export const isPublisherSharePointWebLinkReference = (
  reference: PublisherAttachmentReference
): reference is PublisherSharePointWebLinkReference => reference.schema === 'sharePointWebLinks';

export const isPublisherWebImageReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebImageReference => reference.schema === 'webImages';

export const isPublisherGifReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebImageReference => reference.schema === 'webImages' && reference.gifType !== undefined;

export const isPublisherWebVideoReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebVideoReference => reference.schema === 'webVideos';

export const isPublisherOdspRecentAttachmentReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebLinkReference => isPublisherWebLinkReference(reference) && reference.attachedViaMRU;

export const isPublisherOdspFilePickerAttachmentReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebLinkReference | PublisherSharePointFileLinkReference =>
  (isPublisherWebLinkReference(reference) || isPublisherSharePointAttachmentReference(reference)) &&
  reference.isAttachedViaFilePicker;

export interface PublisherWebImageReference extends WebImageReference {
  readonly gifType?: GifType;
}

export type PublisherWebVideoReference = WebVideoReference;

export type PublisherAttachmentReference =
  | PublisherFileReference
  | PublisherImageFileReference
  | PublisherSharePointFileLinkReference
  | PublisherSharePointVideoLinkReference
  | PublisherSharePointWebLinkReference
  | PublisherVideoFileReference
  | PublisherWebImageReference
  | PublisherWebLinkReference
  | PublisherWebVideoReference
  | FileUploadJobReference
  | ResolvingWebLinkReference;

export type PublisherAttachments = PublisherAttachmentReference[];
